import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CheckCircle, CopySimple } from "@phosphor-icons/react";

import { defaultIconProps } from "../../shared/ui/icons";
import { authorizedRequest, request } from "../../../services/apiClient";
import { BILLING_TOPUP_AUTH_KEY, BILLING_TOPUP_SIGNAL_KEY } from "../constants";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("BillingCheckoutStubPage");

type CheckoutStatus = "idle" | "processing" | "success" | "error";

const pageClasses = "flex min-h-screen items-center justify-center bg-slate-50 p-4";
const cardClasses = "w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center";
const eyebrowClasses = "text-xs font-semibold uppercase tracking-wide text-sky-600";
const titleClasses = "mt-2 text-2xl font-bold text-slate-900";
const subtitleClasses = "mt-2 text-sm text-slate-600";
const referenceClasses = "mt-6 flex flex-col items-center gap-2 rounded-xl bg-slate-50 p-4 border border-slate-100";
const labelClasses = "text-xs font-medium uppercase tracking-wide text-slate-500";
const valueClasses = "font-mono text-lg font-semibold text-slate-900 break-all";
const copyButtonClasses = "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors";
const hintClasses = "mt-6 text-xs text-slate-400";
const actionsClasses = "mt-8 flex flex-col gap-3";
const primaryButtonClasses = "pm-btn pm-btn-primary w-full";
const secondaryButtonClasses = "pm-btn pm-btn-surface w-full";

export function BillingCheckoutStubPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const titleId = useId();
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<CheckoutStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reference = useMemo(
    () => sessionId ?? t("billing.checkout_stub.reference_placeholder"),
    [sessionId, t]
  );

  const signalTopUp = useCallback(() => {
    try {
      localStorage.setItem(
        BILLING_TOPUP_SIGNAL_KEY,
        JSON.stringify({ sessionId, timestamp: Date.now() })
      );
    } catch (error) {
      logger.warn("Unable to signal top up completion", error);
    }
  }, [sessionId]);

  const completeCheckout = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      setErrorMessage(t("billing.checkout_stub.missing_session"));
      return;
    }
    setStatus("processing");
    setErrorMessage(null);
    try {
      const token = (() => {
        try {
          return localStorage.getItem(BILLING_TOPUP_AUTH_KEY);
        } catch (error) {
          logger.warn("Unable to read top-up auth token", error);
          return null;
        }
      })();
      if (token) {
        await authorizedRequest<void>(token, "/credits/purchase/stub/complete", {
          method: "POST",
          body: JSON.stringify({ reference: sessionId })
        });
      } else {
        await request<void>("/credits/purchase/stub/complete", {
          method: "POST",
          body: JSON.stringify({ reference: sessionId })
        });
      }
      signalTopUp();
      try {
        localStorage.removeItem(BILLING_TOPUP_AUTH_KEY);
      } catch (error) {
        logger.warn("Unable to clear top-up auth token", error);
      }
      setStatus("success");
    } catch (error) {
      logger.error("Failed to complete stub checkout", error);
      setStatus("error");
      const message =
        error instanceof Error
          ? error.message
          : t("billing.checkout_stub.error");
      setErrorMessage(message);
    }
  }, [sessionId, signalTopUp, t]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMessage(t("billing.checkout_stub.missing_session"));
      return;
    }
    void completeCheckout();
  }, [completeCheckout, sessionId, t]);

  const handleCopy = async () => {
    if (!sessionId || !navigator.clipboard) {
      return;
    }
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 4000);
    } catch (error) {
      logger.warn("Unable to copy checkout reference", error);
    }
  };

  const handleReturn = () => {
    window.location.assign("/billing");
  };

  const statusClasses = [
    "mt-4 text-sm font-medium",
    status === "success"
      ? "text-green-600"
      : status === "error"
      ? "text-rose-600"
      : "text-slate-500"
  ].join(" ");

  const statusText = (() => {
    if (status === "processing") {
      return t("billing.checkout_stub.processing");
    }
    if (status === "success") {
      return t("billing.checkout_stub.success");
    }
    if (status === "error") {
      return errorMessage ?? t("billing.checkout_stub.error");
    }
    return t("billing.checkout_stub.loading");
  })();

  return (
    <main className={pageClasses} role="main">
      <section className={cardClasses} aria-labelledby={titleId}>
        <p className={eyebrowClasses}>
          {t("billing.checkout_stub.sandbox_label")}
        </p>
        <h1 className={titleClasses} id={titleId}>
          {t("billing.checkout_stub.title")}
        </h1>
        <p className={subtitleClasses}>
          {t("billing.checkout_stub.subtitle")}
        </p>

        <div className={referenceClasses} role="status" aria-live="polite">
          <div className={labelClasses}>
            {t("billing.checkout_stub.reference_label")}
          </div>
          <code className={valueClasses}>{reference}</code>
          <button
            type="button"
            className={copyButtonClasses}
            onClick={handleCopy}
            disabled={!sessionId}
          >
            {copied ? (
              <>
                <CheckCircle {...defaultIconProps} aria-hidden />
                {t("billing.checkout_stub.copied")}
              </>
            ) : (
              <>
                <CopySimple {...defaultIconProps} aria-hidden />
                {t("billing.checkout_stub.copy")}
              </>
            )}
          </button>
        </div>

        <p className={statusClasses}>
          {statusText}
        </p>

        <p className={hintClasses}>
          {t("billing.checkout_stub.hint")}
        </p>

        <div className={actionsClasses}>
          <button
            type="button"
            className={secondaryButtonClasses}
            onClick={completeCheckout}
            disabled={status === "processing"}
          >
            {status === "processing"
              ? t("common.loading")
              : t("billing.checkout_stub.retry")}
          </button>
          <button
            type="button"
            className={primaryButtonClasses}
            onClick={handleReturn}
          >
            {t("billing.checkout_stub.return_cta")}
          </button>
        </div>
      </section>
    </main>
  );
}
